import { useEffect, useRef, useState } from "react"
import { Helmet } from 'react-helmet-async'
import { useTranslation } from "react-i18next"
import clsx from "clsx"
import _ from 'lodash'

import { competitor_family, competitor_member } from '../data'
import Image from '../components/Image'
import { IconChevronDown, IconEdit } from "../components/icons"
import { Outlet, useParams, useNavigate, Link } from "../navigation"
import { useRoles } from "../user/Auth"
import { useComments } from "../comments/CommentsProvider"
import { Comment } from "../comments/Comments"
import { useLocationHistory } from "../navigation/Location"
import { LinkedTabCard } from "../components/TabCard"
import { FocusedBrowser } from "../documents/FocusedBrowser"
import { BreadCrumbs } from "../components/BreadCrumbs"
import DeleteButton from "../components/DeleteButton"
import { emptyStringAsUndefined, truncate } from "../utils/strings"
import { useExternal } from "./"
import { competitorFamilyUrl, competitorMemberUrl } from "./utils"
import { CompetitorFamily, CompetitorMember } from "./external"
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { useAgents } from "../agents/AgentsProvider"
import { nameOf } from "../agents/utils"
import { useProducts } from "../products/ProductsProvider"
import { commodityName, commodityUrl } from "../products/products"
import ProtectionIcon from "../components/ProtectionIcon"
import KanjiTip from "../components/KanjiTip"

// Simple context for the current family
import { createContext, useContext } from "react"
const CompetitorFamilyContext = createContext<CompetitorFamily | null>(null)
function useCompetitorFamily() { return useContext(CompetitorFamilyContext) }

export function FamilyIndex() {
    const { t } = useTranslation()
    const { hasDocuments } = useRoles()

    const { internalReference } = useParams()
    const navigate = useNavigate()
    const { getLastMatchingPath } = useLocationHistory()

    const { familyByReference } = useExternal()

    const family = familyByReference[internalReference]

    useEffect(() => {
        const timer = setTimeout(() => {
            if (family === undefined)
                navigate("/external/portfolio")
        }, 1500)
        return () => clearTimeout(timer)
    }, [family, navigate])

    if (!family) return null

    return <>
        {/* @ts-ignore */}
        <Helmet>
            <title>{t('competitor-family')} {family.internalReference} | Patent Cockpit</title>
        </Helmet>
        <BreadCrumbs parts={[
            { to: (getLastMatchingPath("/external/portfolio?")?.path ?? "..") + '#' + encodeURIComponent(family.internalReference), label: t('external') },
            <CompetitorFamilyBreadCrumb key={family.internalReference} family={family} />,
        ]} />
        <CompetitorFamilyContext.Provider value={family}>
            <div className="overflow-auto grow px-2 sm:px-4 pb-4">
                <div className="w-fit lg:min-w-4xl xl:min-w-6xl" >
                    <LinkedTabCard links={[
                        { to: '.', label: t('overview') },
                        hasDocuments && { to: 'documents', label: t('documents') },
                    ]} />
                </div>
            </div>
        </CompetitorFamilyContext.Provider>
    </>
}

function CompetitorFamilyBreadCrumb({ family }: { family: CompetitorFamily }) {
    const name = emptyStringAsUndefined(family.familyName)
    return (
        <Link to={competitorFamilyUrl(family)} className="modern-h2">
            <span>{family.internalReference}</span>  {name && <span className="hidden lg:inline ml-3 text-pcx-500">{truncate(name)}</span>}
        </Link>
    )
}

export function FamilyOverview() {
    const { isEditUser } = useRoles()
    const family = useCompetitorFamily()

    if (!family) return null

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(40rem,48rem)_45ch] divide-pcx-200 max-lg:divide-y-2 lg:divide-x-2">
            <div className="lg:pr-4 max-lg:pb-4">
                <CompetitorFamilyDetails family={family} isEditUser={isEditUser} />
            </div>
            <div className="max-lg:pt-4 lg:pl-4 max-xl:text-sm">
                <FamilyComments family={family} />
            </div>
            <Outlet />
        </div>
    )
}

function CompetitorFamilyDetails({ family, isEditUser }: { family: CompetitorFamily; isEditUser: boolean }) {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { membersByFamilyId, deleteFamily, reload, agentsByMemberId } = useExternal()
    const { agentById } = useAgents()

    const specificMembers = membersByFamilyId[family.competitorFamilyId] ?? []

    const relevant = specificMembers.filter(m => m.competitorMemberStatus === 'relevant')
    const notRelevant = specificMembers.filter(m => m.competitorMemberStatus === 'not-relevant')
    const inactive = specificMembers.filter(m => m.competitorMemberStatus === 'inactive')

    const memsWithExpiry = specificMembers.map(m => [m.expiryDate, m] as const).filter(([d]) => typeof d === 'string' && /^\d/.test(d))
    const lastExpiry = _.maxBy(memsWithExpiry, ([d]) => d)

    function getAgents(linkType: string) {
        return _(specificMembers)
            .flatMap(m => agentsByMemberId[linkType]?.[m.competitorMemberId] ?? [])
            .uniq()
            .map(a => agentById[a])
            .filter(Boolean)
            .map(a => nameOf(a))
            .uniqWith(_.isEqual)
            .sortBy()
            .value()
    }
    const applicants = getAgents('applicant')
    const inventors = getAgents('inventor')
    const owners = getAgents('owner')

    return <>
        <div className="flex flex-wrap md:flex-nowrap grow items-top gap-2 sm:gap-4 mb-1">
            <div className="grow flex flex-col">
                <h2 className="mb-2">
                    {family.familyName}
                </h2>
            </div>
            {isEditUser &&
                <div className="flex flex-col gap-2 items-end ml-auto">
                    <div className="flex flex-row gap-1">
                        <Link title={t('edit')} to="edit" className="btn-primary p-px w-5 h-5"><IconEdit /></Link>
                        <DeleteButton
                            className="btn-warn p-px w-5 h-5"
                            question={t("delete-family-name", { name: family.internalReference })}
                            deleteAction={async () => {
                                await deleteFamily(family)
                                reload()
                                navigate("../../")
                            }}
                        />
                    </div>
                </div>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-4 border-b-2 border-pcx-200 pb-2">
            <div className="h-36">
                <Image {...{
                    entity: competitor_family,
                    entityId: family.competitorFamilyId,
                    title: `${family.internalReference}: ${family.familyName}`,
                    isEditable: isEditUser,
                    text: family.summary,
                }} />
            </div>
            <p className="whitespace-pre-wrap">{family.summary}</p>
        </div>

        {/* Bibliographic Data */}
        <div className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-x-4 gap-y-2 py-3">
            <h4 className="whitespace-nowrap">{t('bibliographic-data')}</h4>
            <div className="justify-self-end">
                {isEditUser &&
                    <AddMember family={family} members={specificMembers} />}
            </div>

            <MembersOverview members={relevant} title={t('relevant')} />
            <MembersOverview members={notRelevant} title={t('not-relevant')} />
            <MembersOverview members={inactive} title={t('inactive')} />

            {family.externalReference &&
                <KeyValues title={t("externalReference")}>{family.externalReference}</KeyValues>
            }
            <KeyValues title={t("priority-date")}>{family.priorityDate}</KeyValues>
            {lastExpiry &&
                <KeyValues title={t("last-expiry-date")}>
                    {lastExpiry[0]}
                </KeyValues>
            }
            <KeyValues title={t("applicants")} showMore>{applicants.map(i => <AgentItem key={i} agent={i} />)}</KeyValues>
            <KeyValues title={t("inventors")} showMore>{inventors.map(i => <AgentItem key={i} agent={i} />)}</KeyValues>
            <KeyValues title={t("owners")}>{owners.map(i => <AgentItem key={i} agent={i} />)}</KeyValues>
        </div>

        {/* Products */}
        <div className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-x-4 gap-y-2 py-3 mt-3 border-t-2 border-pcx-200">
            <CompetitorFamilyProducts family={family} />
        </div>
    </>
}


function AgentItem({ agent }: { agent: string }) {
    return (
        <span className="inline-flex after:content-[';'] last:after:content-[''] mr-2 last:mr-0">
            <KanjiTip kanjiOpt={agent}><span>{agent}</span></KanjiTip>
        </span>
    )
}

function CompetitorFamilyProducts({ family }: { family: CompetitorFamily }) {
    const { t } = useTranslation()
    const { isEditUser } = useRoles()

    const { competitorCommodityLinks } = useExternal()
    const { commodityById } = useProducts()

    const unclassified = t('unclassified')

    const products = _(competitorCommodityLinks)
        .filter(l => l.competitorFamilyId === family.competitorFamilyId && l.protected)
        .map(l => commodityById[l.commodityId])
        .filter(c => c !== undefined)
        .uniqBy('commodityId')
        .map(c => ({ ...c, commodityName: commodityName(c, unclassified) }))
        .sortBy(c => c.commodityName.toLowerCase())
        .value()

    return <>
        <div className="flex flex-row gap-1 justify-between">
            <h4>{t('products')}</h4>
            {isEditUser && <Link to="products"><IconEdit className="sm:hidden btn-secondary p-px w-5 h-5" /></Link>}
        </div>
        <div className="flex flex-row gap-2">
            <ul className="list-none text-slate-700 pl-0 grow">
                <li className="hidden last:block text-slate-500">{t('no-affected-products')}</li>
                {products.map(({ commodityId, isThirdParty, commodityName: name }) =>
                    <li key={commodityId} className="flex flex-row items-center gap-2">
                        <ProtectionIcon {...{ isThirdParty }} /> <Link to={commodityUrl({ commodityId })}>{name}</Link>
                    </li>)}
            </ul>
            {isEditUser && <Link to="products"><IconEdit className="max-sm:hidden btn-secondary p-px w-5 h-5" /></Link>}
        </div>
    </>
}

function MembersOverview({ members, title }: { members: CompetitorMember[]; title: string }) {
    if (members.length === 0) return null
    return <KeyValues title={title}><MemberLinks members={members} /></KeyValues>
}

function MemberLinks({ members }: { members: CompetitorMember[] }) {
    const { t } = useTranslation()

    const [showMore, setShowMore] = useState(false)
    const [showButton, setShowButton] = useState(false)
    const childrenList = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const isOverflowing = childrenList.current !== undefined ? (childrenList.current?.scrollHeight > childrenList.current?.offsetHeight) : false
        setShowButton(isOverflowing)
    }, [members, childrenList])

    const sorted = _(members).sortBy(m => m.countryCode).value()

    return (
        <div>
            <div ref={childrenList} className={clsx("flex flex-wrap gap-1", showMore ? "" : "max-h-[3.25rem] overflow-hidden")}>
                {sorted.map(m =>
                    <Link
                        className="ribbon ribbon-clickable rounded-sm text-sm py-0.5"
                        key={m.internalReference}
                        to={competitorMemberUrl(m)}
                        title={`${m.countryCode} - ${t(m.competitorMemberStatus)}`}
                    >
                        {m.countryCode}
                    </Link>
                )}
            </div>
            {showButton &&
                <button className="ml-1 text-sm text-slate-500 hover:underline" onClick={() => setShowMore(s => !s)}>
                    {showMore ? t('show-less') : t('show-more')}
                </button>}
        </div>
    )
}

function KeyValues({ title, children, showMore = false }: { title: string; children?: React.ReactNode; showMore?: boolean }) {
    return <>
        <div className="sm:text-right text-gray-500">{title}</div>
        {showMore
            ? <ShowMoreValue>{children}</ShowMoreValue>
            : <div className="pl-4 sm:pl-0 flex flex-wrap gap-1">
                <span className="text-slate-500 hidden last:inline">-</span>
                {children && (typeof children === 'string' ? <span>{children}</span> : children)}
            </div>
        }
    </>
}

function ShowMoreValue({ children }: { children?: React.ReactNode }) {
    const { t } = useTranslation()

    const [showMore, setShowMore] = useState(false)
    const [showButton, setShowButton] = useState(false)
    const childrenList = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const isOverflowing = childrenList.current !== undefined ? (childrenList.current?.scrollHeight > childrenList.current?.offsetHeight) : false
        setShowButton(isOverflowing)
    }, [children, childrenList])

    return (
        <div className="pl-4 sm:pl-0">
            <div ref={childrenList} className={showMore ? "line-clamp-none" : "line-clamp-2"}>
                <span className="text-slate-500 hidden last:inline">-</span>
                {children}
            </div>
            {showButton &&
                <button className="text-sm text-slate-500 hover:underline" onClick={() => setShowMore(s => !s)}>
                    {showMore ? t('show-less') : t('show-more')}
                </button>}
        </div>
    )
}

function AddMember({ family, members }: { family: CompetitorFamily; members: CompetitorMember[] }) {
    const { t } = useTranslation()

    const itemStyle = "px-2 pr-6 py-2 hover:bg-pcx-400 font-medium text-pcx-500 hover:text-white -indent-4 pl-6 whitespace-nowrap"

    return (
        <Menu as="div" className='relative h-fit text-sm'>
            <MenuButton className={clsx(
                'flex flex-row items-center gap-2 rounded-sm border border-l border-pcx-500 p-1 pl-2',
                members?.length > 0 ? 'text-pcx-500 hover:text-white hover:bg-pcx-400' : 'text-white bg-pcx-500 hover:bg-pcx-600'
            )}>
                <span className="whitespace-nowrap">{t("add-competitor-member")}</span>
                <IconChevronDown />
            </MenuButton>
            <MenuItems className='absolute z-10 flex flex-col min-w-full w-fit top-9 right-0 rounded-sm shadow-md border border-pcx-500 bg-white max-h-[13em] overflow-y-auto'>
                <MenuItem>
                    <Link
                        className={itemStyle}
                        to={{
                            pathname: "/external/portfolio/member/add",
                            search: `?competitorFamilyId=${encodeURIComponent(family.competitorFamilyId)}`
                        }}>
                        {t("add-competitor-member")}
                    </Link>
                </MenuItem>
                {_(members)
                    .sortBy(m => m.internalReference)
                    .map((m, i) =>
                        <MenuItem key={`mem-${i}`}>
                            <Link
                                className={itemStyle}
                                to={{
                                    pathname: "/external/portfolio/member/add",
                                    search: `?competitorFamilyId=${encodeURIComponent(family.competitorFamilyId)}&copyFrom=${encodeURIComponent(m.internalReference)}`
                                }}>
                                {`${t('copy-from')} ${m.internalReference} (${m.countryCode})`}
                            </Link>
                        </MenuItem>
                    )
                    .value()}
            </MenuItems>
        </Menu>
    )
}

function FamilyComments({ family }: { family: CompetitorFamily }) {
    const { t } = useTranslation()
    const { isEditUser } = useRoles()

    const { membersByFamilyId } = useExternal()
    const { commentsLookUp } = useComments()

    const specificMembers = membersByFamilyId[family.competitorFamilyId] ?? []

    const memberComments = _(specificMembers)
        .map(member => ({ ...member, comments: commentsLookUp[competitor_member]?.[member.competitorMemberId] ?? [] }))
        .filter(({ comments }) => comments.length > 0)
        .sortBy(m => m.internalReference)
        .value()

    return (
        <div>
            <div className="pb-4 flex flex-row min-w-[20rem]">
                <h4 className="grow">{t('comments')}</h4>
                {isEditUser &&
                    <Link
                        to={`comments?entity=${competitor_family}&entityId=${family.competitorFamilyId}`}
                        className="btn-secondary py-0.5 text-sm whitespace-nowrap"
                        title={t('add-comment')}
                    >
                        {t('add')}
                    </Link>}
            </div>
            <div className="text-sm flex flex-col gap-2">
                {(commentsLookUp[competitor_family]?.[family?.competitorFamilyId] ?? [])
                    .map(c => <Comment key={`comment-${c.commentId}`} {...c} />)
                }
                {memberComments.map(({ internalReference, comments }) =>
                    <Disclosure key={internalReference} as='div' className="mt-2 flex flex-col gap-2">{({ open }) => <>
                        <DisclosureButton className='flex flex-row justify-between items-center px-2 py-1 bg-pcx-100 hover:bg-pcx-200 text-pcx-600 rounded'>
                            <Link className="hover:underline" to={competitorMemberUrl({ internalReference } as CompetitorMember)} >
                                <h5 className="text-pcx-600">{internalReference}</h5>
                            </Link>
                            <IconChevronDown className={clsx('h-5 w-5', open && 'transform rotate-180')} />
                        </DisclosureButton>
                        <DisclosurePanel>
                            {comments.map(c => <Comment key={`comment-${c.commentId}`} {...c} />)}
                        </DisclosurePanel>
                    </>}</Disclosure>
                )}
            </div>
        </div>
    )
}

export function FamilyDocuments() {
    const family = useCompetitorFamily()

    if (!family) return null

    return <FocusedBrowser entity={competitor_family} internalReference={family.internalReference} parentName={family.familyName} />
}
